import type { Server } from 'miragejs';
import { response } from 'mocks/helpers';
import { HTTP_RESPONSE_CODES } from 'mocks/constants';
import { faker } from '@faker-js/faker/locale/en';
import {
  allowedSeasonValues,
  allowedColorFamilyValues,
  itemCustomFieldDefinitions,
  brandOptions,
  organizations,
  suppliers,
  adminUnits,
  categories,
  contactPersons,
  itemNameParts,
  itemDescriptors,
} from './itemsFixtureData';

// Request bodies arrive PascalCased (objCamelToPascal, see lib/api/utils/index.ts and
// api/items.ts) — that conversion is RECURSIVE (every nested object/array key, not just
// top-level), so the inverse here must be too. A shallow-only version left inline-edited
// custom fields (e.g. `data.customFields[].customFieldId`) stored as
// `CustomFields[].CustomFieldId` — invisible to every camelCase reader downstream
// (`row.customFields?.find(cf => cf.customFieldId === …)`), so a saved CF value quietly
// disappeared behind the "_" empty placeholder on the very next refetch even though the
// PUT itself "succeeded". Only top-level fields (name, description, userDefinedId, …)
// have no nested objects, so this stayed invisible until a custom-field save exercised it.
const pascalKeyToCamel = (key: string): string => key.charAt(0).toLowerCase() + key.slice(1);

const pascalBodyToCamel = (body: unknown): unknown => {
  if (Array.isArray(body)) {
    return body.map(item => pascalBodyToCamel(item));
  }
  if (body !== null && typeof body === 'object') {
    return Object.entries(body as Record<string, unknown>).reduce<Record<string, unknown>>(
      (acc, [key, value]) => {
        acc[pascalKeyToCamel(key)] = pascalBodyToCamel(value);
        return acc;
      },
      {}
    );
  }
  return body;
};

const generateCustomFields = (baseId: number) => {
  const count = faker.number.int({ min: 0, max: 3 });
  return Array.from({ length: count }, (_, i) => {
    const allowedValue = faker.helpers.arrayElement(allowedSeasonValues);
    return {
      id: baseId + i,
      customFieldId: 8,
      name: 'Season used',
      userDefinedId: null,
      type: 0,
      value: allowedValue.label,
      itemCustomFieldAllowedValueId: allowedValue.id,
      customFieldAllowedValueUserDefinedID: null,
      isMandatory: false,
      isSupplierEditable: faker.datatype.boolean(),
      internalOnly: false,
      maxChar: null,
      isMultiLine: true,
      allowedDecimals: 0,
      isMultipleAllowedValue: true,
      parentCustomFieldId: null,
      parentCustomFieldName: null,
      allowedValues: allowedSeasonValues,
    };
  });
};

/**
 * Deterministic, filter-friendly item generation. The **filterable** fields (name,
 * number, brand, status, last-updated, quantity) are derived from the row index `id`
 * so the filter/search specs (epic DL-62000) can assert *results*; purely cosmetic
 * fields (description, logo) stay faker-driven for variety. Brand is spread across the
 * name-sorted order (`id % brandOptions.length`) so an unfiltered page shows several
 * brands and a brand filter visibly narrows it to one.
 */
const generateItem = (id: number) => {
  const i = id;
  const part = itemNameParts[i % itemNameParts.length];
  const descriptor = itemDescriptors[Math.floor(i / itemNameParts.length) % itemDescriptors.length];
  const brand = brandOptions[i % brandOptions.length];
  const supplier = suppliers[i % suppliers.length];
  const companyContactPerson = contactPersons[i % contactPersons.length];
  const adminUnit = adminUnits[i % adminUnits.length];
  const categoryCount = (i % 3) + 1; // 1..3 categories
  const selectedCategories = Array.from(
    { length: categoryCount },
    (_, c) => categories[(i + c) % categories.length]
  );
  const hasLogo = i % 4 === 0;
  const hasDeadline = i % 3 === 0;
  const hasErpId = i % 5 === 0;
  const hasPhysicalRef = i % 2 === 0;
  // Spread lastUpdated across every month (01–12) and day (01–28) of 2025, deterministic
  // per row (no runtime clock, so snapshots and the date-range spec stay stable). A
  // 2025-03-15 boundary lands on rows where i ≡ 14 (mod 28) and i ≡ 2 (mod 12) — a known
  // non-empty same-day set for the date-range spec.
  const day = String((i % 28) + 1).padStart(2, '0');
  const month = String((i % 12) + 1).padStart(2, '0');
  const lastUpdated = `2025-${month}-${day}T10:00:00`;

  return {
    id,
    name: `${part} ${descriptor}`,
    userDefinedId: String(1000 + i),
    description: faker.helpers.maybe(() => faker.commerce.productDescription()) ?? null,
    brand: { id: brand.id, name: brand.name },
    organization: organizations[i % organizations.length],
    supplier: { id: supplier.id, name: supplier.name },
    companyContactPerson: { id: companyContactPerson.id, name: companyContactPerson.name },
    supplierContactPerson: null,
    adminUnit,
    logo: hasLogo ? { id: faker.string.uuid(), url: faker.image.url() } : null,
    // ~2/3 active. Derived from the block index `floor(i / brandOptions.length)` rather than
    // `i % 3` so status does NOT correlate with `brand` (`i % brandOptions.length`): were it
    // `i % 3`, a brand count divisible by 3 would make every brand uniformly one status, and a
    // Brand+Status combined filter (DL-62006/DL-62007) could intersect to an empty set. Keying
    // off the block index guarantees every brand carries a mix of Active/Inactive rows.
    isActive: Math.floor(i / brandOptions.length) % 3 !== 0,
    suppliedBy: null,
    readyForExport: i % 2 === 0,
    updatedAfterLastExport: i % 7 === 0,
    lastUpdated,
    deadline: hasDeadline ? '2026-09-15' : null,
    etd: hasDeadline ? '2026-10-01' : null,
    erpItemId: hasErpId ? `ERP-${1000 + i}` : null,
    defaultQty: (i % 15) + 1, // 1..15 — deterministic values inside and outside the 5–10 range
    nameGeneratorExtraText: null,
    physicalReferenceNumber: hasPhysicalRef ? `REF-${id}` : null,
    categories: selectedCategories,
    customFields: generateCustomFields(id * 10),
  };
};

faker.seed(42);

const perPage = 100;

/**
 * Deterministic fixture for Testmo 2373088 — "[Items] Audit log created when inline edit is
 * saved" (DL-61924). Exported so the e2e spec can address it by a known id/name instead of a
 * faker-seeded random row. Must be a REAL entry in `itemsData` (not just an `apiTransform`
 * response payload) because the PUT/audit-log handlers below look the item up by id in this
 * array — a purely `apiTransform`-fabricated item would 404 on save. Prepended (not appended)
 * so it lands on the mocked page-1 slice like any other row; no existing items.* e2e spec
 * asserts exact row content at position 0 (only structural/generic checks), so inserting it
 * is safe for the rest of the suite.
 */
export const auditLogFixtureItem = {
  id: 90101,
  name: 'Audit Log Fixture Item',
  userDefinedId: 'IT-90101',
  description: 'Original fixture description',
  brand: brandOptions[1],
  organization: organizations[0],
  supplier: suppliers[0],
  companyContactPerson: contactPersons[0],
  supplierContactPerson: null,
  adminUnit: adminUnits[0],
  logo: null,
  isActive: true,
  suppliedBy: null,
  readyForExport: false,
  updatedAfterLastExport: false,
  lastUpdated: '2026-01-15T09:00:00',
  deadline: null,
  etd: null,
  erpItemId: null,
  defaultQty: 1,
  nameGeneratorExtraText: null,
  physicalReferenceNumber: null,
  categories: [],
  // Shared non-mixed multiselect CF for dl-59493 bulk-edit demos
  customFields: [
    {
      id: 90101008,
      customFieldId: 8,
      name: 'Season used',
      userDefinedId: null,
      type: 0,
      value: 'AW25',
      itemCustomFieldAllowedValueId: 26,
      customFieldAllowedValueUserDefinedID: null,
      isMandatory: false,
      isSupplierEditable: true,
      internalOnly: false,
      maxChar: null,
      isMultiLine: true,
      allowedDecimals: 0,
      isMultipleAllowedValue: true,
      parentCustomFieldId: null,
      parentCustomFieldName: null,
      allowedValues: allowedSeasonValues,
    },
  ],
};

/**
 * Deterministic fixture pair/trio for Testmo 2373062 — "[Items] Duplicate item combination
 * validation" (DL-61919). Exported so the e2e spec can reference the same identities the
 * PUT handler's duplicate check below compares against, rather than depending on the
 * faker-seeded random rows (whose exact name/supplier text isn't test-controlled).
 *
 * - `duplicateFixtureItemA` / `duplicateFixtureItemB` share a supplier — renaming B to A's
 *   name is expected to collide (same name + same supplier).
 * - `duplicateFixtureItemC` has a DIFFERENT supplier — renaming C to A's name is expected to
 *   succeed (uniqueness is on the name+supplier combination, not name alone).
 *
 * Prepended to `itemsData` (not appended) purely for stable array positions in the fixture
 * data itself — this does NOT guarantee page-1 visibility in the UI. The Items page's
 * default sort is name-ASC (`Items.tsx`'s `useUrlState` initial state), so rendered
 * position is determined alphabetically, not by array order: "Aluminum Zipper QA Fixture"
 * and "Bronze Buckle QA Fixture" happen to sort early/mid enough to land on the first page,
 * but nothing here guarantees that for every fixture name. The e2e spec navigates with
 * `?search=QA Fixture` (a substring common to all three fixture names, absent from every
 * generated name) to make all three rows visible deterministically, regardless of sort
 * order or how many generated rows precede them alphabetically. No existing items.* e2e
 * spec asserts exact row content at these positions (only structural/generic checks — see
 * items-table-columns.spec.ts, items.spec.ts).
 */
export const duplicateFixtureItemA = {
  id: 90001,
  name: 'Aluminum Zipper QA Fixture',
  userDefinedId: 'DUP-1001',
  description: null,
  brand: brandOptions[1],
  organization: organizations[0],
  supplier: suppliers[0],
  companyContactPerson: contactPersons[0],
  supplierContactPerson: null,
  adminUnit: adminUnits[0],
  logo: null,
  isActive: true,
  suppliedBy: null,
  readyForExport: false,
  updatedAfterLastExport: false,
  lastUpdated: '2026-01-15T09:00:00',
  deadline: null,
  etd: null,
  erpItemId: null,
  defaultQty: 10,
  nameGeneratorExtraText: null,
  physicalReferenceNumber: null,
  categories: [],
  customFields: [],
};

export const duplicateFixtureItemB = {
  ...duplicateFixtureItemA,
  id: 90002,
  name: 'Steel Button QA Fixture',
  userDefinedId: 'DUP-1002',
  // Same supplier as Item A (suppliers[0]) — required by the Testmo scenario ("a second item
  // with the same supplier").
};

export const duplicateFixtureItemC = {
  ...duplicateFixtureItemA,
  id: 90003,
  name: 'Bronze Buckle QA Fixture',
  userDefinedId: 'DUP-1003',
  // Different supplier from Item A — the "same name, different supplier saves successfully"
  // adversarial case.
  supplier: suppliers[1],
};

/**
 * Deterministic probes for the bulk-edit conditional field visibility epic (DL-62326):
 * Supplier field editable-only-when-empty (Testmo 1809237/1809238) and Supplier Contact
 * enabled-only-with-shared-supplier (1809239/1809240/1809241). All isolable together via
 * `?search=BulkEditProbe` (unique substring, absent from every generated/other fixture name).
 * `supplier: null` requires the array literal below to include at least one null-supplier
 * entry from the start so `itemsData`'s inferred element type allows it.
 */
export const bulkEditProbeSupplierEmptyA = {
  id: 90210,
  name: 'BulkEditProbe Supplier Empty A',
  userDefinedId: 'IT-90210',
  description: null,
  brand: brandOptions[0],
  organization: organizations[0],
  supplier: null,
  companyContactPerson: contactPersons[0],
  supplierContactPerson: null,
  adminUnit: adminUnits[0],
  logo: null,
  isActive: true,
  suppliedBy: null,
  readyForExport: false,
  updatedAfterLastExport: false,
  lastUpdated: '2026-01-15T09:00:00',
  deadline: null,
  etd: null,
  erpItemId: null,
  defaultQty: 1,
  nameGeneratorExtraText: null,
  physicalReferenceNumber: null,
  categories: [],
  customFields: [],
};

export const bulkEditProbeSupplierEmptyB = {
  ...bulkEditProbeSupplierEmptyA,
  id: 90211,
  name: 'BulkEditProbe Supplier Empty B',
  userDefinedId: 'IT-90211',
};

export const bulkEditProbeSupplierSameA = {
  ...bulkEditProbeSupplierEmptyA,
  id: 90212,
  name: 'BulkEditProbe Supplier Same A',
  userDefinedId: 'IT-90212',
  supplier: suppliers[0],
};

export const bulkEditProbeSupplierSameB = {
  ...bulkEditProbeSupplierSameA,
  id: 90213,
  name: 'BulkEditProbe Supplier Same B',
  userDefinedId: 'IT-90213',
};

export const bulkEditProbeSupplierDiff = {
  ...bulkEditProbeSupplierSameA,
  id: 90214,
  name: 'BulkEditProbe Supplier Diff',
  userDefinedId: 'IT-90214',
  // Different supplier from the "Same" pair — drives the mixed-suppliers disabled case.
  supplier: suppliers[1],
};

/**
 * Deterministic probes for the Hierarchy Mother/Child conditional field visibility epic
 * (DL-62326, Testmo 1809242-1809245): "Color family" (customFieldId 14, mother) / "Color
 * shade" (customFieldId 15, child) — the same cross-field CF pair DL-61900/DL-61901 seeded
 * (see `allowedColorFamilyValues`/`allowedColorShadeValues` above), reused here rather than
 * duplicated. Mother/Same A+B share allowed-value 501 ("Warm"); Diff carries 502 ("Cool").
 */
const colorFamilyCf = (
  cfEntryId: number,
  allowedValueId: number,
  label: string
): ReturnType<typeof generateCustomFields>[number] => ({
  id: cfEntryId,
  customFieldId: 14,
  name: 'Color family',
  userDefinedId: null,
  type: 3,
  value: label,
  itemCustomFieldAllowedValueId: allowedValueId,
  customFieldAllowedValueUserDefinedID: null,
  isMandatory: false,
  isSupplierEditable: true,
  internalOnly: false,
  maxChar: null,
  isMultiLine: false,
  allowedDecimals: 0,
  isMultipleAllowedValue: false,
  parentCustomFieldId: null,
  parentCustomFieldName: null,
  allowedValues: allowedColorFamilyValues,
});

export const bulkEditProbeMotherSameA = {
  ...bulkEditProbeSupplierEmptyA,
  id: 90215,
  name: 'BulkEditProbe Mother Same A',
  userDefinedId: 'IT-90215',
  supplier: suppliers[0],
  customFields: [colorFamilyCf(90215014, 501, 'Warm')],
};

export const bulkEditProbeMotherSameB = {
  ...bulkEditProbeMotherSameA,
  id: 90216,
  name: 'BulkEditProbe Mother Same B',
  userDefinedId: 'IT-90216',
  customFields: [colorFamilyCf(90216014, 501, 'Warm')],
};

export const bulkEditProbeMotherDiff = {
  ...bulkEditProbeMotherSameA,
  id: 90217,
  name: 'BulkEditProbe Mother Diff',
  userDefinedId: 'IT-90217',
  customFields: [colorFamilyCf(90217014, 502, 'Cool')],
};

/**
 * Reads the `apiTransform` fixture's payload (localStorage key `api-transform`, written by
 * `frontend/e2e/src/fixtures`) so handlers that need custom control (bypassing the standard
 * `response()` envelope, or deciding whether to persist a mutation) can inspect the same
 * deviation the spec injected. Mirrors the shape `response()` reads in `mocks/helpers.ts`.
 */
type ApiTransformPayload = {
  url?: string;
  statusCode?: number;
  data?: unknown;
  code?: string;
  error?: { details: { type: string; value: string }[] };
};

const readApiTransform = (): ApiTransformPayload | null => {
  try {
    const raw = localStorage.getItem('api-transform');
    return raw ? (JSON.parse(raw) as ApiTransformPayload) : null;
  } catch {
    return null;
  }
};

/** Returns the active transform when it targets `url` AND forces a failure (>=400), else null. */
const failingTransformFor = (url: string): ApiTransformPayload | null => {
  const transform = readApiTransform();
  if (!transform?.url || !url.includes(transform.url)) return null;
  return typeof transform.statusCode === 'number' && transform.statusCode >= 400 ? transform : null;
};

/**
 * Audit-log entry for an inline-edit save (Testmo 2373088 / DL-61924). No real audit-log
 * endpoint exists yet in this codebase (no UI panel, no BE route) — this is the "minimal
 * endpoint the spec can assert against" the ticket asks for, added here rather than as a
 * standalone feature.
 */
type ItemAuditLogEntry = {
  id: number;
  itemId: number;
  field: string;
  oldValue: string | null;
  newValue: string | null;
  timestamp: string;
  /** The mock's logged-in designer user — see frontend/src/mocks/data/user.ts (`John Smith`). */
  user: string;
};

const itemAuditLogData: ItemAuditLogEntry[] = [];
let itemAuditLogSeq = 1;
const ACTING_USER = 'John Smith';

const recordAuditEntry = (
  itemId: number,
  field: string,
  oldValue: string | null,
  newValue: string | null
): void => {
  if (oldValue === newValue) return; // no-op edit — no entry
  itemAuditLogData.push({
    id: itemAuditLogSeq++,
    itemId,
    field,
    oldValue,
    newValue,
    timestamp: new Date().toISOString(),
    user: ACTING_USER,
  });
};

/** Inline-edit-tracked fields: incoming PascalCase body key -> Item field name. */
const AUDITED_FIELDS: Record<string, string> = {
  Name: 'name',
  Description: 'description',
  UserDefinedId: 'userDefinedId',
};

/** Case/whitespace-insensitive match, mirroring how a real uniqueness constraint would compare. */
const normalizeForDuplicateCheck = (value: unknown): string =>
  typeof value === 'string' ? value.trim().toLowerCase() : '';

const itemsData = [
  auditLogFixtureItem,
  duplicateFixtureItemA,
  duplicateFixtureItemB,
  duplicateFixtureItemC,
  bulkEditProbeSupplierEmptyA,
  bulkEditProbeSupplierEmptyB,
  bulkEditProbeSupplierSameA,
  bulkEditProbeSupplierSameB,
  bulkEditProbeSupplierDiff,
  bulkEditProbeMotherSameA,
  bulkEditProbeMotherSameB,
  bulkEditProbeMotherDiff,
  ...Array.from({ length: 213 }, (_, i) => generateItem(i + 1)),
];

/**
 * Deterministic probe item for the CF hierarchy E2E spec (DL-61900 / DL-61901): a fixed id and a
 * single-word, unique name ("HierarchyProbe") isolable via a plain `?search=HierarchyProbe` (no
 * generated item name/descriptor part contains it, and it needs no URL-encoding), with no Color
 * family/shade value seeded so the child CF starts disabled. Pushed onto the real mutable
 * `itemsData` array (not injected per-test via `apiTransform`) so the PUT handler's write is what
 * a subsequent GET/refetch reflects — a real round trip, not a static override.
 */
const cfHierarchyProbeItemId = 9101;
itemsData.push({
  id: cfHierarchyProbeItemId,
  name: 'Color HierarchyProbe item',
  userDefinedId: 'IT-9101',
  description: null,
  brand: brandOptions[0],
  organization: organizations[0],
  supplier: suppliers[0],
  companyContactPerson: contactPersons[0],
  supplierContactPerson: null,
  adminUnit: adminUnits[0],
  logo: null,
  isActive: true,
  suppliedBy: null,
  readyForExport: false,
  updatedAfterLastExport: false,
  lastUpdated: '2026-01-15T09:00:00',
  deadline: null,
  etd: null,
  erpItemId: null,
  defaultQty: 1,
  nameGeneratorExtraText: null,
  physicalReferenceNumber: null,
  categories: [],
  customFields: [],
});

/** Resolve the sortable value for an item given a column id (`sort` param). */
const getSortValue = (item: (typeof itemsData)[number], key: string): unknown => {
  switch (key) {
    case 'name':
      return item.name;
    case 'userDefinedId':
      return item.userDefinedId;
    case 'description':
      return item.description;
    case 'defaultQty':
      return item.defaultQty;
    case 'lastUpdated':
      return item.lastUpdated;
    case 'isActive':
      return item.isActive;
    case 'readyForExport':
      return item.readyForExport;
    case 'brand':
      return item.brand?.name;
    case 'supplier':
      return item.supplier?.name;
    case 'companyContactPerson':
      return item.companyContactPerson?.name;
    case 'supplierContactPerson':
      return (item.supplierContactPerson as { name?: string } | null)?.name;
    case 'adminUnit':
      return item.adminUnit?.name;
    default:
      return (item as Record<string, unknown>)[key];
  }
};

/** Deterministic comparator: nullish/empty sink to the end, numeric/boolean aware, else locale string. */
const compareValues = (a: unknown, b: unknown): number => {
  const aEmpty = a == null || a === '';
  const bEmpty = b == null || b === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return a === b ? 0 : a ? 1 : -1;
  return String(a).localeCompare(String(b), 'en', { numeric: true, sensitivity: 'base' });
};

/**
 * Server-side sort emulation over the deterministic dataset. Returns the input
 * untouched when no `sort` is requested so the unsorted default order is preserved.
 */
const sortItems = (
  data: typeof itemsData,
  sort: string | null,
  order: string | null
): typeof itemsData => {
  if (!sort) return data;
  const direction = (order ?? 'ASC').toUpperCase() === 'DESC' ? -1 : 1;
  return [...data].sort(
    (a, b) => direction * compareValues(getSortValue(a, sort), getSortValue(b, sort))
  );
};

const includesCI = (haystack: string | null | undefined, needle: string): boolean =>
  (haystack ?? '').toLowerCase().includes(needle.toLowerCase());

/** Comma-separated numeric id list → Set<number> (brand, supplier, category, unit). */
const numericIdSet = (raw: string): Set<number> =>
  new Set(
    raw
      .split(',')
      .map(v => Number(v))
      .filter(v => Number.isFinite(v))
  );

/** Comma-separated string id list → Set<string> (contact-person uuids). */
const stringIdSet = (raw: string): Set<string> =>
  new Set(
    raw
      .split(',')
      .map(v => v.trim())
      .filter(Boolean)
  );

/**
 * Server-side filtering matching the query-param contract built by
 * `frontend/src/features/items/api/getItems.ts`. Text params are case-insensitive
 * "contains"; id params are exact (comma-separated union); `lastupdated` is an inclusive
 * `yyyy-MM-dd,yyyy-MM-dd` range on the date-only slice (timezone-independent); `defaultqty`
 * is an inclusive `<from>_<to>` numeric range — the number filter serializes with an
 * UNDERSCORE (see `getValueForUrl` in the table filter transforms), not a comma. Custom-field
 * (`cf_*`) params are intentionally not filtered here — no spec in this epic asserts a
 * CF-filtered result set, and dropping rows on an unmodelled param would be worse than
 * ignoring it. The free-text `search` (name + number) preserves the previous behaviour.
 */
const applyFilters = (data: typeof itemsData, params: URLSearchParams): typeof itemsData => {
  let rows = data;

  const search = params.get('search');
  if (search)
    rows = rows.filter(r => includesCI(r.name, search) || includesCI(r.userDefinedId, search));

  const name = params.get('name');
  if (name) rows = rows.filter(r => includesCI(r.name, name));

  const userDefinedId = params.get('userDefinedId');
  if (userDefinedId) rows = rows.filter(r => includesCI(r.userDefinedId, userDefinedId));

  const description = params.get('description');
  if (description) rows = rows.filter(r => includesCI(r.description, description));

  const isActive = params.get('isActive');
  if (isActive != null && isActive !== '') {
    const want = isActive === '1' || isActive.toLowerCase() === 'true';
    rows = rows.filter(r => Boolean(r.isActive) === want);
  }

  const brand = params.get('brand');
  if (brand) {
    const set = numericIdSet(brand);
    rows = rows.filter(r => r.brand != null && set.has(r.brand.id));
  }

  const supplier = params.get('supplier');
  if (supplier) {
    const set = numericIdSet(supplier);
    rows = rows.filter(r => r.supplier != null && set.has(r.supplier.id));
  }

  const companyContactPerson = params.get('companycontactperson');
  if (companyContactPerson) {
    const set = stringIdSet(companyContactPerson);
    rows = rows.filter(r => r.companyContactPerson != null && set.has(r.companyContactPerson.id));
  }

  const category = params.get('category');
  if (category) {
    const set = numericIdSet(category);
    rows = rows.filter(r => r.categories.some(c => set.has(c.id)));
  }

  const unit = params.get('unit');
  if (unit) {
    const set = numericIdSet(unit);
    rows = rows.filter(r => r.adminUnit != null && set.has(r.adminUnit.id));
  }

  const readyForExport = params.get('readyforexport');
  if (readyForExport != null && readyForExport !== '') {
    const want = readyForExport === '1' || readyForExport.toLowerCase() === 'true';
    rows = rows.filter(r => Boolean(r.readyForExport) === want);
  }

  const lastUpdated = params.get('lastupdated');
  if (lastUpdated) {
    const [from, to] = lastUpdated.split(',');
    rows = rows.filter(r => {
      const day = r.lastUpdated.slice(0, 10);
      return (!from || day >= from) && (!to || day <= to);
    });
  }

  const defaultQty = params.get('defaultqty');
  if (defaultQty) {
    const [minRaw, maxRaw] = defaultQty.split('_');
    const min = minRaw === '' || minRaw == null ? -Infinity : Number(minRaw);
    const max = maxRaw === '' || maxRaw == null ? Infinity : Number(maxRaw);
    rows = rows.filter(r => r.defaultQty >= min && r.defaultQty <= max);
  }

  return rows;
};

/**
 * Merge a PUT body's (partial) `customFields` array onto the item's existing entries by
 * `customFieldId`, upserting rather than replacing the whole array. The real inline-edit save
 * path only ever sends the *changed* custom fields (`diffCustomFieldsForApi` trims the row's
 * full array down to the deltas), so a naive `{ ...item, ...body }` spread — replacing the
 * whole `customFields` array with just that delta — would silently drop every other CF value
 * the item held (e.g. saving the "Color shade" child would erase the "Color family" mother).
 * Grouping by `customFieldId` (not a 1:1 replace) also keeps multi-value CFs (multiselect —
 * several entries sharing one `customFieldId`) correct: every entry for a touched id is
 * replaced by the incoming set for that id, and every untouched id's entries are kept as-is.
 */
const mergeCustomFields = (
  existing: (typeof itemsData)[number]['customFields'],
  incoming: unknown
): (typeof itemsData)[number]['customFields'] => {
  if (!Array.isArray(incoming)) return existing;
  const touchedIds = new Set(
    incoming
      .map(cf =>
        cf && typeof cf === 'object' ? (cf as { customFieldId?: number }).customFieldId : undefined
      )
      .filter((id): id is number => id != null)
  );
  const kept = existing.filter(cf => !touchedIds.has(cf.customFieldId));
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- incoming is the request-body shape, already camelCased
  return [...kept, ...(incoming as (typeof itemsData)[number]['customFields'])];
};

export const itemsEndpoints = (server: Server) => {
  server.get('/api/meta/items/customfields', (_schema, request) =>
    response(request.url, {
      customFieldDefinitions: itemCustomFieldDefinitions,
    })
  );

  server.get('/api/items', (_schema, request) => {
    const url = new URL(request.url, 'http://localhost');
    const cursor = url.searchParams.get('cursor');

    // Pagination is cursor-based and `useItemList` sends *only* `cursor` on later
    // pages, so the cursor must be self-describing: it encodes the page index plus
    // the FULL filter+sort query. Re-applying the exact same filters+sort on every
    // page keeps a multi-page filtered/sorted view coherent (a filter matching more
    // than one page must not leak non-matching rows onto page 2), and keeps a
    // reloaded/bookmarked paginated view aligned with the sort/filter the URL shows.
    let page = 0;
    let params: URLSearchParams;

    if (cursor) {
      // Cursor form: `<page>~<encodeURIComponent(filter+sort query)>`. Split on the
      // FIRST `~` only — `URLSearchParams.toString()` percent-encodes `~` (%7E) and
      // `encodeURIComponent` adds none, so the encoded query never carries a bare `~`.
      const sep = cursor.indexOf('~');
      page = Number(cursor.slice(0, sep)) || 1;
      params = new URLSearchParams(decodeURIComponent(cursor.slice(sep + 1)));
    } else {
      // First page: take the filter+sort params off the URL (drop pagination keys).
      params = new URLSearchParams(url.search);
      params.delete('cursor');
      params.delete('pageSize');
    }

    const sort = params.get('sort');
    const order = params.get('order');
    const result = sortItems(applyFilters(itemsData, params), sort, order);
    const start = page * perPage;
    const end = Math.min(start + perPage, result.length);
    const pageData = result.slice(start, end);
    const hasMore = end < result.length;

    const nextCursor = `${page + 1}~${encodeURIComponent(params.toString())}`;

    return response(request.url, pageData, {
      status: 'success',
      code: 'ok',
      pagination: {
        cursor: hasMore ? nextCursor : '',
        hasMore,
      },
      error: { details: [] },
      statusCode: 200,
    });
  });

  /**
   * GET /ItemNameNumberGenerator/Item/Settings — org-level name/number auto-generation flags
   * (src/features/items/api/useItemGeneratorSettings.ts). Not previously mocked: an unhandled
   * request here passes through to the real backend (frontend/src/mocks/browser.ts general
   * passthrough) and fails in the e2e/mock-only environment, so the hook's `?? false` fallback
   * always won — meaning `isNameAutoGenerated` / `isNumberAutoGenerated` could never be forced
   * on for a test. This endpoint returns raw JSON (no `{status,code,data}` envelope — the hook
   * reads the fields directly off the response body), overridable via `apiTransform`'s `data`.
   */
  server.get('/ItemNameNumberGenerator/Item/Settings', (_schema, request) => {
    const transform = readApiTransform();
    const overridden = !!transform?.url && request.url.includes(transform.url);
    const body =
      overridden && transform?.data
        ? transform.data
        : {
            adminItemNameGeneratorSettings: { isActive: false },
            adminItemNumberGeneratorSettings: { isActive: false },
          };
    return response(request.url, undefined, {
      statusCode: overridden && transform?.statusCode ? transform.statusCode : 200,
      body,
    });
  });

  /**
   * PUT /api/items/:id — inline-edit save.
   *
   * Request body arrives PascalCased (`objCamelToPascal` in src/api/items.ts converts the
   * outgoing payload's keys before it reaches apisauce), so incoming fields are read as
   * `Name`, `Description`, `UserDefinedId`, etc.
   *
   * Enforces the duplicate-combination rule for Testmo 2373062 / DL-61919 first: a name that,
   * case/whitespace-normalized, matches another item's name AND whose supplier (unchanged by
   * these tests, so read off the existing row) also matches, is rejected with a 409 in the
   * team error envelope before any mutation or audit recording happens.
   *
   * Otherwise records an audit-log entry (DL-61924) for each tracked field whose value actually
   * changed — a no-op edit (same value resubmitted) records nothing — then merges every incoming
   * field onto the existing row; customFields are upserted by customFieldId (mergeCustomFields,
   * DL-61875 epic) rather than replaced wholesale, so a partial inline-edit body doesn't drop the
   * item's other custom-field values.
   *
   * When the active `apiTransform` forces a failure status for this URL, the mutation and
   * audit logging are skipped entirely before `response()` builds the (overridden) error
   * envelope, so a failed save can never appear to have been persisted or audited.
   */
  server.put('/api/items/:id', (_schema, request) => {
    const id = Number(request.params.id);
    const index = itemsData.findIndex(item => item.id === id);

    if (index === -1) {
      return response(request.url, undefined, {
        statusCode: HTTP_RESPONSE_CODES.ENTITY_NOT_FOUND,
        code: 'resource_error.item_not_found',
        status: 'fail',
        error: {
          details: [{ type: 'resource_error.item_not_found', value: 'Item not found.' }],
        },
      });
    }

    const existing = itemsData[index];

    let body: Record<string, unknown> = {};
    try {
      body = JSON.parse(request.requestBody || '{}') as Record<string, unknown>;
    } catch {
      body = {};
    }

    // Duplicate-combination check (Testmo 2373062 / DL-61919) runs before any mutation or
    // audit recording, so a rejected save never appears to have been persisted or audited.
    // Supplier isn't mutated by any inline-edit path exercised in the e2e suite (it's
    // read-only once an item already has one — see config-cols.tsx), so the duplicate check
    // reads the item's own current supplier.
    const incomingName = typeof body.Name === 'string' ? body.Name : existing.name;
    const supplierId = existing.supplier?.id ?? null;
    const normalizedIncomingName = normalizeForDuplicateCheck(incomingName);
    const isDuplicate = itemsData.some(
      other =>
        other.id !== id &&
        normalizeForDuplicateCheck(other.name) === normalizedIncomingName &&
        (other.supplier?.id ?? null) === supplierId
    );

    if (isDuplicate) {
      return response(request.url, undefined, {
        statusCode: 409,
        status: 'error',
        code: 'resource_conflict_error.item_already_exists',
        error: {
          details: [
            {
              type: 'resource_conflict_error.item_already_exists',
              value:
                'An item with this combination already exists. Try a different name, number, or supplier.',
            },
          ],
        },
      });
    }

    if (!failingTransformFor(request.url)) {
      // Record audit entries for the tracked fields BEFORE the general merge below
      // overwrites `existing`, so prevValue reflects the pre-save state.
      for (const [incomingKey, field] of Object.entries(AUDITED_FIELDS)) {
        if (!(incomingKey in body)) continue;
        const nextValue = body[incomingKey];
        if (typeof nextValue !== 'string' && nextValue !== null) continue;
        const record = existing as unknown as Record<string, string | null>;
        const prevValue = record[field] ?? null;
        if (nextValue === prevValue) continue;
        recordAuditEntry(id, field, prevValue, nextValue);
      }

      // Merge every incoming field (not just the audited ones) so non-audited fields
      // (Deadline, Etd, etc.) still persist instead of being silently dropped; customFields
      // are upserted by customFieldId (mergeCustomFields) rather than replaced wholesale, so a
      // partial inline-edit body doesn't drop the item's other custom-field values.
      const { customFields: incomingCustomFields, ...restCamelBody } = pascalBodyToCamel(
        body
      ) as Record<string, unknown>;
      itemsData[index] = {
        ...existing,
        ...restCamelBody,
        customFields: mergeCustomFields(existing.customFields, incomingCustomFields),
        id,
      } as typeof existing;
    }

    return response(request.url, itemsData[index], {
      statusCode: HTTP_RESPONSE_CODES.UPDATED,
      code: 'ok',
      status: 'success',
      error: { details: [] },
    });
  });

  /**
   * PUT /api/items — bulk-edit save (Testmo 1577222 / DL-62335 "modal functions correctly
   * after refactor"). Payload is a PascalCased array, one entry per selected item, each
   * carrying `Id` plus only the fields the bulk-edit form actually changed
   * (`useBulkEditSubmit`). Mirrors PUT /api/items/:id's partial-merge behaviour (customFields
   * upserted by customFieldId, not replaced wholesale) but over N rows in one request, and
   * skips the mutation entirely when the active apiTransform forces a failure for this URL —
   * so a failed "Apply changes" can never appear to have been persisted.
   */
  server.put('/api/items', (_schema, request) => {
    let body: unknown = [];
    try {
      body = JSON.parse(request.requestBody || '[]');
    } catch {
      body = [];
    }
    const rows = (Array.isArray(body) ? body : [body]) as Record<string, unknown>[];

    if (!failingTransformFor(request.url)) {
      rows.forEach(row => {
        if (!row || typeof row !== 'object') return;
        const id = Number(row.Id);
        const index = itemsData.findIndex(item => item.id === id);
        if (index === -1) return;
        const existing = itemsData[index];
        const { customFields: incomingCustomFields, ...restCamelBody } = pascalBodyToCamel(
          row
        ) as Record<string, unknown>;
        itemsData[index] = {
          ...existing,
          ...restCamelBody,
          customFields: mergeCustomFields(existing.customFields, incomingCustomFields),
          id,
        } as typeof existing;
      });
    }

    const updatedIds = new Set(rows.map(row => Number(row?.Id)).filter(id => Number.isFinite(id)));
    const updatedRows = itemsData.filter(item => updatedIds.has(item.id));

    return response(request.url, updatedRows, {
      statusCode: HTTP_RESPONSE_CODES.UPDATED,
      code: 'ok',
      status: 'success',
      error: { details: [] },
    });
  });

  /**
   * GET /api/items/:id/audit-log — minimal audit-log read surface for DL-61924. Returns every
   * recorded entry for the item, oldest first (insertion order), so the spec can assert field,
   * old/new value, timestamp, and acting user after an inline-edit save.
   */
  server.get('/api/items/:id/audit-log', (_schema, request) => {
    const id = Number(request.params.id);
    const entries = itemAuditLogData.filter(entry => entry.itemId === id);
    return response(request.url, entries, {
      statusCode: 200,
      status: 'success',
      code: 'ok',
      error: { details: [] },
    });
  });
};
